import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { jobsApi, usersApi } from '../services/api';
import { Job, User } from '../types';

const Recordings: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobsApi.getJobs(
        page + 1,
        rowsPerPage,
        statusFilter || undefined,
        searchQuery || undefined,
        userFilter || undefined
      );
      setJobs(response.data);
      setTotal(response.total);
      setError(null);
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Jobs load error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, searchQuery, userFilter]);

  const loadUsers = useCallback(async () => {
    try {
      const response = await usersApi.getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleUserFilterChange = (event: any) => {
    setUserFilter(event.target.value);
    setPage(0);
  };

  const handleRetryTranscription = async (job: Job) => {
    try {
      await jobsApi.retryTranscription(job.id);
      loadJobs(); // Reload to get updated status
    } catch (err) {
      console.error('Retry failed:', err);
    }
  };

  const handleDeleteJob = async (job: Job) => {
    if (window.confirm(`Are you sure you want to delete "${job.originalName}"?`)) {
      try {
        await jobsApi.deleteJob(job.id);
        loadJobs();
      } catch (err) {
        console.error('Delete failed:', err);
      }
    }
  };

  const handleDownloadTranscription = async (job: Job) => {
    try {
      const blob = await jobsApi.downloadTranscription(job.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job.originalName}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'primary';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    const totalMinutes = Math.floor(seconds / 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Jobs
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search jobs"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>User</InputLabel>
            <Select
              value={userFilter}
              label="User"
              onChange={handleUserFilterChange}
            >
              <MenuItem value="">All Users</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {job.filename}
                    </Typography>
                    {job.meetingTitle && (
                      <Typography variant="caption" color="textSecondary">
                        {job.meetingTitle}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      color={getStatusColor(job.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDuration(job.duration)}</TableCell>
                  <TableCell>{formatFileSize(job.fileSize)}</TableCell>
                  <TableCell>
                    {formatDistanceToNow(new Date(job.uploadedAt), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedJob(job);
                            setViewDialogOpen(true);
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Play job">
                        <IconButton size="small">
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>

                      {job.status === 'completed' && (
                        <Tooltip title="Download transcription">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadTranscription(job)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {job.status === 'failed' && (
                        <Tooltip title="Retry transcription">
                          <IconButton
                            size="small"
                            onClick={() => handleRetryTranscription(job)}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Delete job">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteJob(job)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Job Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedJob && (
          <>
            <DialogTitle>
              Job Details
              <Typography variant="body2" color="textSecondary">
                {selectedJob.originalName}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Metadata
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedJob.status}
                      color={getStatusColor(selectedJob.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Duration
                    </Typography>
                    <Typography variant="body1">
                      {formatDuration(selectedJob.duration)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      File Size
                    </Typography>
                    <Typography variant="body1">
                      {formatFileSize(selectedJob.fileSize)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Uploaded
                    </Typography>
                    <Typography variant="body1">
                      {formatDistanceToNow(new Date(selectedJob.uploadedAt), { addSuffix: true })}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {selectedJob.meetingTitle && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Meeting Information
                  </Typography>
                  <Typography variant="body1">
                    {selectedJob.meetingTitle}
                  </Typography>
                  {selectedJob.participants && selectedJob.participants.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Participants:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        {selectedJob.participants.map((participant, index) => (
                          <Chip key={index} label={participant} size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {selectedJob.transcription && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Transcription
                  </Typography>
                  <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedJob.transcription}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedJob.errorMessage && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom color="error">
                    Error
                  </Typography>
                  <Alert severity="error">
                    {selectedJob.errorMessage}
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
              {selectedJob.status === 'completed' && (
                <Button
                  onClick={() => {
                    handleDownloadTranscription(selectedJob);
                    setViewDialogOpen(false);
                  }}
                  startIcon={<DownloadIcon />}
                >
                  Download
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Recordings; 